import { useState, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Plus, MoreHorizontal, Trash2, Edit, Clock, Archive, GripVertical, AlertCircle, CheckCircle2, Circle, CheckSquare, MessageCircle, Timer, Repeat, Link2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { format, isPast, isToday, isTomorrow } from "date-fns";
import { PRIORITY_CONFIG, formatTime } from "@/utils/constants";
import { Avatar, MentionText } from "@/components/shared";

const TaskCard = ({ task, categories, tags, contacts, members, onEdit, onDelete, onToggleComplete, onArchive, onStartTime, onStopTime, onCompleteRecurring, dragHandleProps, selected, onToggleSelect, selectionActive, onDiscuss }) => {
  const category = categories.find(c => c.id === task.category_id);
  const taskTags = tags.filter(t => task.tag_ids?.includes(t.id));
  const allAssignable = members?.length > 0 ? members : contacts;
  const assignees = allAssignable.filter(m => task.assignee_ids?.includes(m.id));
  const priorityConfig = PRIORITY_CONFIG[task.priority];
  const completedSubtasks = task.subtasks?.filter(st => st.completed).length || 0;
  const totalSubtasks = task.subtasks?.length || 0;
  const commentCount = task.comments?.length || 0;
  const isTimerActive = task.time_entries?.some(e => !e.ended_at);
  const watcherCount = task.watcher_ids?.length || 0;

  const getDueDateStatus = () => {
    if (!task.due_date) return null;
    const dueDate = new Date(task.due_date);
    if (task.completed) return { className: "text-muted-foreground", label: format(dueDate, 'MMM d') };
    if (isPast(dueDate) && !isToday(dueDate)) return { className: "text-red-600 font-semibold", label: "Overdue" };
    if (isToday(dueDate)) return { className: "text-amber-600 font-semibold", label: task.due_time ? `Today ${task.due_time}` : "Today" };
    if (isTomorrow(dueDate)) return { className: "text-blue-600", label: task.due_time ? `Tomorrow ${task.due_time}` : "Tomorrow" };
    return { className: "text-muted-foreground", label: task.due_time ? `${format(dueDate, 'MMM d')} ${task.due_time}` : format(dueDate, 'MMM d') };
  };
  const dueDateStatus = getDueDateStatus();

  return (
    <div className={`task-card group ${isTimerActive ? 'ring-2 ring-primary ring-offset-2' : ''} ${selected ? 'ring-2 ring-primary ring-offset-1' : ''}`} data-testid={`task-card-${task.id}`}>
      {task.cover_color && <div className="task-cover-band" style={{ backgroundColor: task.cover_color }} data-testid={`task-cover-${task.id}`} />}
      <div className="flex items-start gap-2">
        {onToggleSelect && (
          <div
            className={`mt-1 flex-shrink-0 transition-opacity ${selectionActive || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(task.id)} data-testid={`task-select-${task.id}`} className="h-4 w-4" />
          </div>
        )}
        <button className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-primary transition-colors" {...dragHandleProps}><GripVertical className="h-4 w-4" /></button>
        <button onClick={() => task.is_recurring ? onCompleteRecurring(task) : onToggleComplete(task)} className="mt-0.5 flex-shrink-0">
          {task.completed ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 onClick={() => onEdit(task)} className={`font-semibold text-sm leading-tight cursor-pointer hover:text-primary transition-colors ${task.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}><MentionText text={task.title} /></h4>
            {task.is_recurring && <Repeat className="h-3 w-3 text-primary" title="Recurring task" />}
            {watcherCount > 0 && <span className="flex items-center gap-0.5 text-xs text-muted-foreground" title={`${watcherCount} watching`}><Eye className="h-3 w-3" />{watcherCount}</span>}
          </div>
          {task.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2 mt-1"><MentionText text={task.description} /></p>}
          {totalSubtasks > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><CheckSquare className="h-3 w-3" /><span>{completedSubtasks}/{totalSubtasks}</span></div>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all rounded-full" style={{ width: `${(completedSubtasks / totalSubtasks) * 100}%` }} /></div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary" className={`text-xs px-2 py-0.5 font-medium ${priorityConfig.className}`}>{priorityConfig.label}</Badge>
            {category && <Badge variant="outline" className="text-xs px-2 py-0.5"><span className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: category.color }} />{category.name}</Badge>}
            {taskTags.slice(0, 2).map(tag => <Badge key={tag.id} variant="secondary" className="text-xs px-2 py-0.5" style={{ backgroundColor: `${tag.color}15`, color: tag.color }}>{tag.name}</Badge>)}
            {task.blocked_by?.length > 0 && <span className="flex items-center gap-1 text-xs text-red-500" title={`Blocked by ${task.blocked_by.length} task(s)`}><AlertCircle className="h-3 w-3" />Blocked</span>}
            {task.blocking?.length > 0 && <span className="flex items-center gap-1 text-xs text-blue-500" title={`Blocking ${task.blocking.length} task(s)`}><Link2 className="h-3 w-3" />{task.blocking.length}</span>}
          </div>
          {(dueDateStatus || commentCount > 0 || task.total_time_minutes > 0 || assignees.length > 0) && (
            <div className="flex items-center justify-between gap-2 mt-2.5 pt-2 border-t border-border/50">
              <div className="flex items-center gap-2.5 min-w-0">
                {dueDateStatus && <span className={`flex items-center gap-1 text-xs ${dueDateStatus.className}`} data-testid={`task-due-${task.id}`}><Clock className="h-3 w-3" />{dueDateStatus.label}</span>}
                {commentCount > 0 && <span className="flex items-center gap-1 text-xs text-muted-foreground"><MessageCircle className="h-3 w-3" />{commentCount}</span>}
                {task.total_time_minutes > 0 && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Timer className="h-3 w-3" />{formatTime(task.total_time_minutes)}</span>}
              </div>
              {assignees.length > 0 && (
                <div className="flex items-center gap-1 shrink-0" data-testid={`task-assignee-inline-${task.id}`} title={assignees.map(a => a.name).join(', ')}>
                  <div className="flex -space-x-1.5">{assignees.slice(0, 3).map(a => a.picture ? <img key={a.id} src={a.picture} alt={a.name} className="w-6 h-6 rounded-full border-2 border-card" title={a.name} /> : <Avatar key={a.id} name={a.name} color={a.avatar_color} size="sm" />)}</div>
                  {assignees.length > 3 && <span className="text-xs text-muted-foreground ml-1">+{assignees.length - 3}</span>}
                </div>
              )}
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(task)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
            {onDiscuss && <DropdownMenuItem onClick={() => onDiscuss(task)} data-testid={`task-discuss-${task.id}`}><MessageCircle className="h-4 w-4 mr-2" />Discuss</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => onArchive(task)}><Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(task.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const IS_TOUCH = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

export const SortableTaskCard = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.task.id, disabled: IS_TOUCH });
  const [swipeX, setSwipeX] = useState(0);
  const touchRef = useRef(null);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  if (!IS_TOUCH) {
    return <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="select-none cursor-grab active:cursor-grabbing"><TaskCard {...props} dragHandleProps={listeners} /></div>;
  }

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, horizontal: null };
  };
  const onTouchMove = (e) => {
    const s = touchRef.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (s.horizontal === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) s.horizontal = Math.abs(dx) > Math.abs(dy);
    if (s.horizontal) setSwipeX(Math.max(-140, Math.min(140, dx)));
  };
  const onTouchEnd = () => {
    const s = touchRef.current;
    touchRef.current = null;
    if (s?.horizontal && Math.abs(swipeX) > 60 && props.onSwipeMove) props.onSwipeMove(props.task, swipeX > 0 ? 1 : -1);
    setSwipeX(0);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: swipeX ? `translateX(${swipeX}px) rotate(${swipeX / 45}deg)` : undefined,
        transition: swipeX ? 'none' : 'transform 0.25s ease',
        touchAction: 'pan-y',
        opacity: swipeX ? Math.max(0.6, 1 - Math.abs(swipeX) / 400) : 1,
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="select-none"
      data-testid={`swipeable-${props.task.id}`}
    >
      <TaskCard {...props} dragHandleProps={{}} />
    </div>
  );
};

export const KanbanColumn = ({ column, tasks, categories, tags, contacts, members, onEditTask, onDeleteTask, onToggleComplete, onArchiveTask, onAddTask, onEditColumn, onDeleteColumn, totalColumns, onStartTime, onStopTime, onCompleteRecurring, selectedTaskIds, onToggleSelectTask, onSwipeMove, onDiscuss }) => {
  const columnTasks = tasks.filter(task => task.column_id === column.id);
  const selectionActive = selectedTaskIds && selectedTaskIds.size > 0;
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({ id: column.id });
  return (
    <div className="kanban-column" style={{ '--column-color': column.color }}>
      <div className="kanban-column-header">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: column.color }} />
          <span className="font-semibold text-sm">{column.name}</span>
          <span className="text-xs text-muted-foreground bg-muted/80 px-2 py-0.5 rounded-full font-medium">{columnTasks.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-primary/10 hover:text-primary" onClick={() => onAddTask(column.id)}><Plus className="h-4 w-4" /></Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditColumn(column)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
              {totalColumns > 1 && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => onDeleteColumn(column.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem></>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ScrollArea className="flex-1 pr-2">
        <SortableContext items={columnTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          <div ref={setDroppableRef} className={`space-y-2.5 ${columnTasks.length === 0 ? 'min-h-[300px]' : 'min-h-[100px]'} rounded-lg transition-colors ${isOver ? 'bg-primary/5 ring-2 ring-primary/30 ring-dashed' : ''}`}>
            {columnTasks.length === 0 ? <div className="text-center py-8 text-muted-foreground text-xs border-2 border-dashed border-primary/20 rounded-lg bg-primary/5">Drop tasks here</div> :
              columnTasks.map(task => <SortableTaskCard key={task.id} task={task} categories={categories} tags={tags} contacts={contacts} members={members} onEdit={onEditTask} onDelete={onDeleteTask} onToggleComplete={onToggleComplete} onArchive={onArchiveTask} onStartTime={onStartTime} onStopTime={onStopTime} onCompleteRecurring={onCompleteRecurring} selected={selectedTaskIds?.has(task.id)} onToggleSelect={onToggleSelectTask} selectionActive={selectionActive} onSwipeMove={onSwipeMove} onDiscuss={onDiscuss} />)}
          </div>
        </SortableContext>
      </ScrollArea>
    </div>
  );
};
